import { atom, useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import type { ReactNode } from 'react';
import { LINK_LABELS, type NavBarPath } from 'src/constants';
import { useSidebarLinks } from '@/components/SideBar/useSidebarLinks/useSidebarLinks';
import { getSideBarLinkId } from '@/utils/navigation';

interface PageBanner {
  id: string;
  banner: ReactNode;
}

export const contentHeaderState = atomFamily(() => atom<ReactNode>(undefined));

export const pageBannerState = atomFamily(() => atom<PageBanner[]>([]));

export function useAddPageBannerToState() {
  return useAtomCallback((_, set, banner: PageBanner, interactionId: string) =>
    set(pageBannerState(interactionId), (prev) => {
      if (prev) {
        return [...prev, banner];
      }
      return [banner];
    }),
  );
}

export function useClearPageBannerById(interactionId: string) {
  return useAtomCallback((_, set, bannerId: string) => {
    set(pageBannerState(interactionId), (prev) => {
      if (prev) {
        return prev.filter(({ id }) => id !== bannerId);
      }
      return [];
    });
  });
}

export function useSetContentHeader(interactionId: string) {
  return useAtomCallback((_, set, header: ReactNode) => {
    set(contentHeaderState(interactionId), header);
  });
}

export const openSideBarSectionsState = atom<Record<string, boolean>>({
  [LINK_LABELS.COVER_SHEET]: true,
});

export const coverSheetNavSectionsState = atomFamily(() =>
  atom<NavBarPath[]>([]),
);

export function useSetCoverSheetNavSections() {
  const { links } = useSidebarLinks();
  return useAtomCallback(
    (_, set, id: string, sections: { label: string; section: string }[]) => {
      set(
        coverSheetNavSectionsState(id),
        sections.map((s) => ({
          ...s,
          path: links.cover_sheet?.pathname ?? '',
        })),
      );
    },
  );
}

export interface MissingFieldsData {
  section: string;
  label: string;
  name: string;
  index: number;
  isAddable: boolean;
  cardTitle?: string;
  cardId?: string;
  contactId?: string;
}

export const sideBarLinkBadgeCountState = atomFamily(() =>
  atom<number | undefined>(undefined),
);

export const missingFieldsInformation = atom<MissingFieldsData[]>([]);

export function useSetSideBarLinkBadgeCount() {
  return useAtomCallback(
    (
      _,
      set,
      interactionId: string,
      linkNames: (string | undefined)[],
      count: number,
      missingFields: MissingFieldsData[],
    ) => {
      const sideBarLinkId = getSideBarLinkId(interactionId, linkNames);
      set(sideBarLinkBadgeCountState(sideBarLinkId), count);
      set(missingFieldsInformation, missingFields);
    },
  );
}

export const activeInteractionSectionState = atomFamily(() =>
  atom<{ section: string; isScrollingToViaSidebar: boolean } | undefined>(
    undefined,
  ),
);

export const useActiveInteractionSectionState = (interactionId: string) =>
  useAtom(activeInteractionSectionState(interactionId));

export const missingFieldLinkCardButtonId = atom<string | null>(null);

export const missingFieldId = atom<string | null>(null);
